import Vue from "vue"
import { validDeviceWorkout } from "./healthDevice"
import { store } from "@/store/stores"
import ownHealthFeatureAccess from "./ownHealthFeatureAccess"
import healthConnect from "./healthConnectService"
import healthImport from "./appleHealthImportService"
import mescanService from "./mescanHealthService"
import { healthDay, moveHealthDay, parseHealthHistory } from "./healthStatistics"
import { healthSearchDay } from "./healthImportMatching"
import { customerHealthSeries, customerHealthRange } from "./customerHubHealthData"
import RangeCache from "./customerHubRangeCache"

const emptyCoverage = () => ({ from: "", to: "", requestedFrom: "", requestedTo: "", limited: true, complete: false, message: "Noch keine Health-Daten geladen." })

// A read-only adapter over the existing device services. Raw values remain in
// memory. Calling this never authorizes, links, exports or persists a workout.
class CustomerHubHealthAdapter {
  constructor() {
    this.state = Vue.observable({ workouts: [], coverage: emptyCoverage(), busy: false, error: "" })
    this.metricState = Vue.observable({ series: [], coverage: emptyCoverage(), busy: false, error: "" })
    this.version = 0; this.metricVersion = 0
    this.workoutPending = null; this.metricPending = null; this.metricCache = null
    this.workoutCache = new RangeCache(8, 20000)
  }
  scope() {
    const access = ownHealthFeatureAccess.capture(), health = healthConnect.state
    return access && healthConnect.isDevice() && !(health.ios && health.busy) && health.enabled && health.ownerId === ownHealthFeatureAccess.state.userId
      ? `${access}|${health.lastSyncAt || ""}|${health.permissionKey}|${health.historyPermissionGranted}|${[...health.grantedCategories].sort().join(",")}|${mescanService.scope()}` : ""
  }
  start() {
    if (this.unwatch) return
    this.unwatch = store.watch(() => this.scope(), () => this.reset())
  }
  stop() { this.unwatch?.(); this.unwatch = null; this.reset() }
  resetActivities() {
    this.version++
    this.workoutPending = null
    Object.assign(this.state, { workouts: [], coverage: emptyCoverage(), busy: false, error: "" })
  }
  reset() {
    this.invalidateActivities()
    this.resetActivities(); this.metricVersion++
    this.metricPending = null; this.metricCache = null
    Object.assign(this.metricState, { series: [], coverage: emptyCoverage(), busy: false, error: "" })
  }
  getCoverage() { return this.state.coverage }
  historyFrom() { return healthConnect.state.ios || healthConnect.state.historyPermissionGranted ? "2000-01-01" : moveHealthDay(healthDay(), -29) }
  invalidateActivities() { this.workoutCache.clear() }
  getMetricSeries() { return this.metricState.series }
  unavailable(from, to) {
    const message = !healthConnect.isDevice() ? "Health-Werte sind in der iPhone- und Android-App verfügbar."
      : !healthConnect.state.enabled ? `${healthConnect.providerName()} ist nicht verbunden.`
      : "Für diese Datenart liegen keine freigegebenen Werte vor."
    return { ...emptyCoverage(), requestedFrom: from, requestedTo: to, message }
  }
  loadRange(from, to) {
    this.start()
    const key = `${this.scope()}|${healthConnect.state.lastSyncAt || ""}|${from}|${to}`
    if (this.workoutPending?.key === key) return this.workoutPending.promise
    const cached = this.workoutCache.get(key), ttl = to < healthDay() ? 300000 : 30000
    if (this.scope() && cached && Date.now() - cached.at < ttl) {
      this.version++
      this.workoutPending = null
      Object.assign(this.state, cached.value, { busy: false, error: "" })
      return Promise.resolve(this.state)
    }
    const request = { key, promise: null }
    this.workoutPending = request
    request.promise = this.loadWorkoutRange(from, to).then(result => {
      if (this.workoutPending === request && !result.error && result.coverage.from && !result.coverage.truncated && result.coverage.linkStatusAvailable) this.workoutCache.put(key, { workouts: result.workouts, coverage: result.coverage }, result.workouts.length)
      return result
    }).finally(() => { if (this.workoutPending === request) this.workoutPending = null })
    return request.promise
  }
  async loadWorkoutRange(from, to) {
    const version = ++this.version, scope = this.scope()
    const current = () => version === this.version && scope === this.scope()
    Object.assign(this.state, { workouts: [], busy: false, error: "", coverage: this.unavailable(from, to) })
    if (!scope || !healthConnect.state.grantedCategories.includes("workouts")) return this.state
    this.state.busy = true
    try {
      const range = customerHealthRange(from, to)
      let lower = from < this.historyFrom() ? this.historyFrom() : from
      let limited = lower > from || healthConnect.state.ios, readableFrom = ""
      let truncated = false, linkStatusAvailable = true
      const workouts = new Map()
      if (!range.futureOnly && lower <= range.to) {
        if (!await healthImport.available()) throw new Error("Die Health-Freigabe konnte nicht bestätigt werden. Bitte die Verbindung prüfen.")
        if (!current()) return this.state
        const result = await healthImport.searchRange({ from: Math.max(Date.UTC(2000, 0, 1), healthSearchDay(lower).from), to: healthSearchDay(range.to).to },
          { allowOffline: true, isCurrent: current })
        if (!current()) return this.state
        readableFrom = result.readableFrom
        if (readableFrom && Date.parse(readableFrom) > healthSearchDay(from).from) {
          limited = true
          lower = healthDay(new Date(readableFrom)) > lower ? healthDay(new Date(readableFrom)) : lower
        }
        truncated = result.truncated
        linkStatusAvailable = result.linkStatusAvailable
        for (const workout of result.workouts || []) {
          const start = Date.parse(workout.startedAt), finish = Date.parse(workout.endedAt)
          if (!validDeviceWorkout(workout) ||
            !Number.isFinite(start) || !Number.isFinite(finish) || finish <= start || finish > Date.now()) continue
          workouts.set(workout.workoutId, workout)
        }
      }
      if (!current()) return this.state
      this.state.workouts = [...workouts.values()].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))
      this.state.coverage = { from: lower <= range.to ? lower : "", to: range.to, requestedFrom: from, requestedTo: to,
        readableFrom, limited, truncated, linkStatusAvailable,
        complete: !limited && !truncated && linkStatusAvailable,
        message: [limited ? (healthConnect.state.ios ? "Apple Health zeigt die verfügbaren Aufzeichnungen für diesen Zeitraum. Fehlende Trainings können auch an Lesefreigaben liegen." : healthConnect.state.historyPermissionGranted ? "Die installierte App begrenzt den abrufbaren Verlauf. Für ältere Trainings bitte die App aktualisieren." : "Ältere Android-Trainings benötigen die Freigabe für den Verlauf.") : "", truncated ? "Viele Aufzeichnungen: Es wird ein Ausschnitt gezeigt." : "",
          !linkStatusAvailable ? "Gespeicherte Zuordnungen konnten nicht vollständig geprüft werden." : ""].filter(Boolean).join(" ") }
    } catch (error) {
      if (current()) { this.state.error = error.message || "Health-Aktivitäten konnten nicht geladen werden."; this.state.coverage.complete = false }
    } finally { if (current()) this.state.busy = false }
    return this.state
  }
  loadMetricsRange(from, to, force = false) {
    this.start()
    const key = `${this.scope()}|${from}|${to}|${healthConnect.state.lastSyncAt || ""}`
    if (this.metricPending?.key === key) return this.metricPending.promise
    if (!force && this.metricCache?.key === key && Date.now() - this.metricCache.at < 30000) return Promise.resolve(this.metricState)
    const request = { key, promise: null }
    this.metricPending = request
    request.promise = this.loadMetricsRequest(from, to).then((result) => {
      if (this.metricPending === request && !result.error && result.coverage.from) this.metricCache = { key, at: Date.now() }
      return result
    }).finally(() => { if (this.metricPending === request) this.metricPending = null })
    return request.promise
  }
  async loadMetricsRequest(from, to) {
    const version = ++this.metricVersion, scope = this.scope()
    const current = () => version === this.metricVersion && scope === this.scope()
    Object.assign(this.metricState, { series: [], busy: false, error: "", coverage: this.unavailable(from, to) })
    if (!scope || !healthConnect.state.historySupported) return this.metricState
    this.metricState.busy = true
    try {
      const range = customerHealthRange(from, to)
      // Keep an explicit, bounded range. Whole-history selection reports its
      // device coverage; it does not launch thousands of native reads.
      const historyFrom = moveHealthDay(range.today, -healthConnect.historyDays())
      const lower = from < historyFrom ? historyFrom : from, histories = []
      let limited = lower > from
      healthConnect.applyStatus(await healthConnect.native("healthConnectConfigure"))
      if (!current()) return this.metricState
      await mescanService.loadExternal()
      if (!current()) return this.metricState
      if (!range.futureOnly) for (let day = lower; day <= range.to; day = moveHealthDay(day, 31)) {
        const end = moveHealthDay(day, 30) < range.to ? moveHealthDay(day, 30) : range.to
        const raw = await healthConnect.native("healthConnectHistory", { from: day, to: end }, 45000)
        if (!current()) return this.metricState
        const history = parseHealthHistory(raw, { from: day, to: end })
        histories.push(history); limited = limited || history.limited
      }
      if (!current()) return this.metricState
      const snapshot = healthConnect.state.snapshot
      const ownsBody = mescanService.state.scope === mescanService.scope()
      this.metricState.series = customerHealthSeries({ histories,
        snapshot: snapshot && snapshot.day >= lower && snapshot.day <= range.to ? snapshot : null,
        records: ownsBody ? mescanService.state.records.filter(item => healthDay(new Date(item.time)) >= lower && healthDay(new Date(item.time)) <= range.to) : [],
        scans: ownsBody ? mescanService.state.scans.filter(item => healthDay(new Date(item.time)) >= from && healthDay(new Date(item.time)) <= range.to) : [] })
      const readable = histories.flatMap(history => history.days).filter(day => day.readable && !day.future)
      this.metricState.coverage = { from: lower <= range.to ? lower : "", to: range.to, requestedFrom: from, requestedTo: to,
        limited, complete: !limited, readableDays: readable.length,
        message: `${readable.length} abrufbare Tage${limited ? " · begrenzter Health-Zeitraum" : ""}. Fehlende Messwerte werden nicht als null gewertet. Quellen und Messmethoden bleiben getrennt.` }
    } catch (error) {
      if (current()) { this.metricState.error = error.message || "Health-Verläufe konnten nicht geladen werden."; this.metricState.coverage.complete = false }
    } finally { if (current()) this.metricState.busy = false }
    return this.metricState
  }
}

export default new CustomerHubHealthAdapter()
